import { addDays, addMonths, addWeeks, addYears, endOfDay, format } from 'date-fns';
import Papa from 'papaparse';

import { GlobalAccount, TenantTeamMember } from '@ndvr/auth-api';
import { TaskCommentMentionRequest, TaskView } from '@ndvr/customer-api';

import { formatDate, titleCase } from 'utils';
import { downloadFile } from 'utils/file-download';

import { TaskViewState, TimePeriodUnit } from './task-state';

const ASSIGNEE_COLORS = ['#F5FFE0', '#E8F1FF', '#EBFFF3', '#FFF6EB', '#E8FFFF', '#FCECEB'];

const CSV_FORMULA_CHARS = /^[=+\-@\t\r]/;
const sanitizeCsvCell = (value: string): string => (CSV_FORMULA_CHARS.test(value) ? `'${value}` : value);

export type TaskAssigneeFields = Pick<TaskView, 'assignee' | 'assigneeName'>;

/** Display name of a tenant team member offered as an assignee. */
export const getTaskAssigneeName = (member: TenantTeamMember): string =>
    member.name || member.email || 'Unknown Assignee';

/** Label for the assignee column, CSV export and details view. */
export const getTaskAssigneeLabel = (task: TaskAssigneeFields): string => {
    if (!task.assignee) return 'Unassigned';
    return task.assigneeName || 'Unknown Assignee';
};

/** First letter of the first and last name part, mirroring first-name + last-name initials. */
export const getTaskAssigneeInitials = (task: TaskAssigneeFields): string => {
    const parts = task.assigneeName?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (!parts.length) return '?';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    return (parts[0][0] + last).toUpperCase();
};

export const getAssigneeColor = (guid: string): string => {
    let hash = 0;
    for (let i = 0; i < guid.length; i++) {
        hash = (hash + guid.charCodeAt(i)) % ASSIGNEE_COLORS.length;
    }
    return ASSIGNEE_COLORS[hash];
};

export const exportTasksCsv = (tasks: TaskView[], householdMap: Map<string, GlobalAccount>): void => {
    const fields = ['title', 'household', 'assignee', 'dueDate', 'type', 'status'];
    const data = tasks.map((task) => {
        const householdName =
            (task.householdGUID ? householdMap.get(task.householdGUID)?.globalAccountNamePlain : undefined) ||
            'No household';
        const assigneeName = getTaskAssigneeLabel(task);
        return [
            sanitizeCsvCell(task.title || 'Untitled Task'),
            sanitizeCsvCell(householdName),
            sanitizeCsvCell(assigneeName),
            formatDate(task.dueDate),
            titleCase(task.type),
            task.status,
        ];
    });
    const csv = Papa.unparse({ fields, data });
    downloadFile(
        `tasks_table_export_${format(new Date(), 'MM_dd_yyyy')}.csv`,
        window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
    );
};

export const getDueDate = (taskViewState: TaskViewState): Date => {
    const { timePeriodValue, timePeriodUnit } = taskViewState;
    const now = new Date();
    switch (timePeriodUnit) {
        case TimePeriodUnit.DAY:
            return endOfDay(addDays(now, timePeriodValue));
        case TimePeriodUnit.WEEK:
            return endOfDay(addWeeks(now, timePeriodValue));
        case TimePeriodUnit.MONTH:
            return endOfDay(addMonths(now, timePeriodValue));
        case TimePeriodUnit.YEAR:
            return endOfDay(addYears(now, timePeriodValue));
        default:
            return now;
    }
};

export const extractMentionsFromHtml = (content: string): TaskCommentMentionRequest[] => {
    if (!content || typeof DOMParser === 'undefined') {
        return [];
    }
    const doc = new DOMParser().parseFromString(content, 'text/html');
    const mentions = new Map<string, TaskCommentMentionRequest>();
    doc.querySelectorAll<HTMLElement>('[data-type="mention"][data-id]').forEach((element) => {
        const personOrgGUID = element.dataset.id;
        if (!personOrgGUID || mentions.has(personOrgGUID)) return;
        mentions.set(personOrgGUID, { internalUserProfileGUID: personOrgGUID });
    });
    return [...mentions.values()];
};
